import { BicyclingLayer, Circle, GoogleMap, InfoWindow, LoadScript, MapContext, Marker, Polygon, TransitLayer, useLoadScript } from '@react-google-maps/api';
import * as React from 'react';
import { createContext, useContext, useEffect, useImperativeHandle, useState } from 'react';
import { EpiSettings } from '../../dto/epi-settings';
import { CHICAGO, mapControllerDefaultState, calculateCenterOfMarkers,  MAP_FOCUS_POINT, MAP_FOCUS_POINT_CLOSE, MAP_RESET, MAP_SET_PROXIMITY, MAP_FOCUS_NETWORK_NO_POLYGONE, getNoBoundaryZoomByMarkersArray } from '../../services/map-controller';
import { getMapName, METERS_PER_MILE, LongPress, addrFromLatLng } from '../../services/maps-api';
import { getAvailability, getEsHs } from '../../utility/api-utilities';
import HideLogBoundary from '../error/hide-log-boundary';
import { AppContext } from '../school-search-app';
import { mapStyles } from './map-container-config';
import SchoolMapMarker from './school-map-marker';
import { PAGE_MODE } from '../../interfaces'
import { getPageProperties } from '../../services/cps-school-api';
export interface ISchoolMapProps {
    currentLocation;

}
 
export const SchoolMapContext = createContext(null)

function HoverPolygon(props) {
    const passedProps = {
        ...props,
        options: {
            ...props.options
        }
    }
    const [hovered, setHovered] = useState(false)
    const [clicked, setClicked] = useState(false)
    const [boxPos, setBoxPos] = useState(null)
    const mapContext = useContext(SchoolMapContext);

    const onMouseOver = e => {
        setHovered(true);
    }
    const onMouseOut = e => {
        setHovered(false);
    }
    const onClick = e => {
        setBoxPos({
            lat: e.latLng.lat(),
            lng: e.latLng.lng(),
        })
        setClicked(true)
        mapContext.closeNotifications(props.notificationKey);
    }

    useEffect(() => {
        if (mapContext.nextNotification == props.notificationKey) {
            setClicked(true)
        } else {
            setClicked(false)
        }
    }, [mapContext.notificationReset])
    passedProps.options.strokeWeight = passedProps.options.strokeWeight * (hovered ? 3 : 1);
    passedProps.options.strokeWeightHL = passedProps.options.strokeWeightHL * (hovered ? 3 : 1);


    return <><Polygon {...passedProps}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
        onClick={onClick}
        key={`refreshContents${props.options.Name}`}
    />
        {clicked && <InfoWindow
            key={`refreshContents_marker${props.options.Name}`}
            position={boxPos}
            onCloseClick={() => {
                setClicked(false)
            }}
        >
            <div className="map-popup">
                {passedProps.children}
            </div>
        </InfoWindow>}
    </>
}



function getMapGeometry(feature, overlay, uniqueIndex, multiLevel) {
    if (feature.type == 'Feature') {
        switch (feature.geometry.type) {
            case 'Polygon':
                const i = 0;
                let coords = [];

                coords = feature.geometry.coordinates.map(coord => coord.map(arr => ({ lat: arr[1], lng: arr[0] })));

                return <HoverPolygon
                    paths={coords}
                    options={feature.properties}
                    key={`hover_${feature.properties.Name}_${multiLevel}_${uniqueIndex}_${i}}`}
                    notificationKey={`hover_${feature.properties.Name}_${uniqueIndex}_${multiLevel}_${i}}`}
                >
                    {overlay.config.clickContent && overlay.config.clickContent(feature.properties)}
                </HoverPolygon>;
            case 'MultiPolygon':
                return feature.geometry.coordinates.map((coord, i) => <HoverPolygon
                    paths={coord[0].map(arr => ({ lat: arr[1], lng: arr[0] }))}

                    options={feature.properties}
                    key={`hover_${feature.properties.Name}_${multiLevel}_${uniqueIndex}_${i}}`}
                    notificationKey={`hover_${feature.properties.Name}_${multiLevel}_${uniqueIndex}_${i}}`}
                >
                    {overlay.config.clickContent && overlay.config.clickContent(feature.properties)}
                </HoverPolygon>);
            case 'GeometryCollection':
                if (feature.geometry.geometries) {
                    const collection = feature.geometry.geometries
                        .map((geo, j) => {
                            const mockFeat = {
                                type: 'Feature',
                                geometry: geo,
                                properties: feature.properties
                            }
                            return getMapGeometry(mockFeat, overlay, uniqueIndex, j)
                        })
                    return collection;
                }
                return <></>

        }
    }
}

const defaultMapCenter = {
    lat: CHICAGO.lat,
    lng: CHICAGO.lng - .15
};

declare type Libraries = ("drawing" | "geometry" | "localContext" | "places" | "visualization")[];
const libraries = ['places'] as Libraries;

function SchoolMap({ schools, overlays, focus, checkedSchools, height, selectedSchoolId }) {

    const [selectedSchool, setSelectedSchool] = useState(null);
    const [map, setMap] = useState(null);
    const [allFeatures, setAllFeatures] = useState([]);
    const [notificationReset, setNotificationReset] = useState(Date.now())
    const [nextNotification, setNextNotification] = useState(null)
    const [adjustedCameraCenter, setAdjustedCameraCenter] = useState(defaultMapCenter)
    const [adjustedZoom, setAdjustedZoom] = useState(12.5)
    const [isInitialCentered, setInitialCentered] = useState(false);
    const appContext = useContext(AppContext)
    const [schoolProfilePageUrl, setSchoolProfilePageUrl] = useState(null);





    const containerStyle = {
        width: '100%',
        height: height
    };


    useEffect(() => {
        if (map) {
            map.setZoom(appContext.mapController.camera.zoom)
            const center = getAdjustedMapCenter(appContext.mapController.camera.center, appContext.mapController.camera.zoom);

            map.panTo(center);                  
        }
    }, [appContext.mapController.camera])

    // these probably should be wrapped up in AppContext next time we clean upcode
    useEffect(() => {
        getPageProperties().then(props => {
            if (props.schoolProfilePageUrl) {
                setSchoolProfilePageUrl(props.schoolProfilePageUrl);
            }
        })
    }, [])

    useEffect(() => {
        if (appContext.centeredMap) {
            let schoolMap = [];
            const schoolIds = schools.map(s => {
                schoolMap[s._epi.id] = s;
                return '' + s._epi.id
            });

            const markers = allFeatures.filter(feature => {
                return schoolIds.includes(feature.id);
            });
            const mapDim = { width: 1000, height: 650 } //Desktop normal map hight is about 800px, we need to exclude header, and add some padding, so in result got 650px
            const zoomAndCenter = getNoBoundaryZoomByMarkersArray(markers.map(m => m.geometry.coordinates), mapDim)
            appContext.mapControllerDispatch({ action: MAP_FOCUS_NETWORK_NO_POLYGONE, point: zoomAndCenter.center, zoom: zoomAndCenter.zoom })
            appContext.setCenteredMap(false);
        }
    }, [appContext.centeredMap])

    function getAdjustedMapCenter(point, zoom) {
        if (!map || !map.getProjection())
            return point;

        const resultsWrapper = document.querySelector(".results-overlay");
        const resultsArea = document.querySelector(".results-area");
        const searchContainer = document.querySelector(".school-search.container");
        const query = new URLSearchParams((window.location.search).toString())
        const schoolSearchId = query.get("schoolsearchid")

        const blockedWidth = resultsWrapper.clientWidth;
        const blockedHeight = resultsWrapper.clientHeight;
        const resultsWidth = resultsArea.clientWidth;
        const resultsHeight = resultsArea.clientHeight;
        const openHeight = searchContainer.clientHeight;

        let offsetX = 0;
        let offsetY = (openHeight - resultsHeight) / 2; //adjust for search bar

        // adjust the x offset first
        if (resultsWidth < blockedWidth) {
            offsetX = resultsWidth / 2;
        }
        // only do a y offset if there is no x offset already. This accounts for a half-width result panel that may not reach the bottom. We do not want to offset y in that case
        else if (resultsHeight < blockedHeight) {
            offsetY = offsetY + (resultsHeight / 2);
        }

        const d: number = window.innerWidth > 767 ? 0 : 100;
        //const offsetCenter = getOffsetPoint(point, offsetX, offsetY - d);
       const offsetCenter = moveLatLngByPixels(map, point, offsetX, offsetY - d);
       return {
            lng: offsetCenter.lng(),
            lat: offsetCenter.lat()
        }
    }

    function getOffsetPoint(latlng, offsetX, offsetY) {
        var point1 = map.getProjection().fromLatLngToPoint(
            latlng
        );
        var point2 = new google.maps.Point(
            ((typeof (offsetX) == 'number' ? offsetX : 0) / Math.pow(2, map.getZoom())) || 0,
            ((typeof (offsetY) == 'number' ? offsetY : 0) / Math.pow(2, map.getZoom())) || 0
        );
        return map.getProjection().fromPointToLatLng(new google.maps.Point(
            point1.x - point2.x,
            point1.y + point2.y
        ));
    }

    function moveLatLngByPixels(map, latLng, offsetX, offsetY) {
        const zoom = map.getZoom();
        const projection = map.getProjection();
        const scale = Math.pow(2, zoom);

        // Convert lat/lng to world coordinates
        const point = projection.fromLatLngToPoint(latLng);
        const worldCoordinate = new google.maps.Point(point.x * scale, point.y * scale);

        // Apply the pixel offset
        // Add offsetX for right movement and offsetY for downward movement
        const newWorldCoordinate = new google.maps.Point(worldCoordinate.x - offsetX, worldCoordinate.y - offsetY);

        // Convert back to lat/lng
        const newLatLng = projection.fromPointToLatLng(new google.maps.Point(newWorldCoordinate.x / scale, newWorldCoordinate.y / scale));

        return newLatLng;
    }

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: EpiSettings.googleMapsKey,
        libraries: libraries
    })

    const openSchoolProfile = (id => {
        appContext.openSchoolProfile(id);
    })

    useEffect(() => {
        setSelectedSchool(null);
    }, [appContext.mapController.pin]);

    useEffect(() => {
        if (map) {
            new LongPress(map, 1500);
            google.maps.event.addListener(map, 'longpress', function (event) {
                appContext.isLongPress(true)
                addrFromLatLng(event.latLng).then((data) => {
                    //  const posalCodeObj = data['address_components'].find(i => i.types.some(a => a === "postal_code"))
                    const address = data['formatted_address'];

                    /* if (posalCodeObj) {
                         const postalCode = posalCodeObj.long_name;
                         appContext.addZipOverlay(postalCode);
                     }*/

                    if (address) {
                        appContext.setSearchTermOverride(address);
                    };
                });
            })
        }
    }, [map])

    useEffect(() => {
        if (!focus || !allFeatures) return;
        try {
            const feature = allFeatures.filter(s => s.properties.School_ID == focus)[0];

            const point = {
                lng: feature.geometry.coordinates[0],
                lat: feature.geometry.coordinates[1]
            };


            appContext.mapControllerDispatch({ action: MAP_FOCUS_POINT_CLOSE, point: point });

            const school = schools.filter(s => s._epi.id == focus)[0];

            selectSchool(school, {
                lng: feature.geometry.coordinates[0],
                lat: feature.geometry.coordinates[1]
            });
        } catch (e) {
            console.log('error focusing', e)
        }


    }, [focus]);


    const onLoad = (map) => {
        setMap(map);

        Promise.all([
            getMapName('CPS_PREK', 2022, appContext.mode),
            getMapName('DFSS', 2022, appContext.mode),
            getMapName('SCHOOL', 2022, appContext.mode)
        ]).then(res => {
            const schoolMap = new Map();

            // Collect all features and ensure uniqueness by School_ID
            res.forEach(i => {
                i.features.forEach(f => {
                    const schoolId = f.properties.School_ID;
                    if (!schoolMap.has(schoolId)) {
                        schoolMap.set(schoolId, { ...f, id: schoolId });
                    }
                });
            });
            // Convert Map values to an array for setAllFeatures
            const allFeatures = Array.from(schoolMap.values());
            
            setAllFeatures(allFeatures);
        });
    };

    useEffect(() => {
        if (!isInitialCentered && schools.length > 0) {
            setInitialCentered(true);
            appContext.mapControllerDispatch({ action: MAP_RESET });
        }
    }, [schools])

    const getMarkers = () => {
        let schoolMap = [];
        const schoolIds = schools.map(s => {
            schoolMap[s._epi.id] = s;
            return '' + s._epi.id
        });
        const markers = allFeatures.filter(feature => {
            return schoolIds.includes(feature.id);
        }).map(feature => {
            const checked = checkedSchools.map(s => s._epi.id).includes(feature.id);
            const query = new URLSearchParams((window.location.search).toLowerCase());
            const schoolSearchId = query.get("schoolsearchid");
            if (schoolSearchId && schoolSearchId !== feature.id) {
                return <></>;
            }
            return <HideLogBoundary key={feature.id}><SchoolMapMarker
                school={schoolMap[feature.id]}
                checked={checked}
                selectSchool={selectSchool}
                feature={feature}
            /></HideLogBoundary>
        });
        return markers
    };

    useEffect(() => {
        if (appContext.mapSelectedSchoolId == null) {
            setSelectedSchool(null)
        }
    }, [appContext.mapSelectedSchoolId])



    const selectSchool = (school, pos) => {

        appContext.setMapSelectedSchoolId(school)
        setSelectedSchool({
            school: school,
            position: pos
        });
        appContext.addSchoolOverlay(school._epi.id);
    }


    const getDrawableOverlay = (selectedOverlay) => {
        if (selectedOverlay.config.label == 'Bike Paths') {
            return <BicyclingLayer />
        }
        if (selectedOverlay.config.label == 'Transit') {
            return <TransitLayer />
        }
        return selectedOverlay.features.map((feature, i) => getMapGeometry(feature, selectedOverlay, i, 0))
    }

    useEffect(() => {
        closeNotifications(null);
    }, [appContext.selectedOverlay])

    const closeNotifications = (key) => {
        setNextNotification(key);
        setNotificationReset(Date.now());
    }

    useEffect(() => {
        if (appContext.selectedOverlay === null) {
            setNextNotification(null);
            setNotificationReset(null);
        }
    }, [appContext.selectedOverlay])

    useEffect(() => {
        if (isLoaded) {
            appContext.setIsMapLoaded(true);
        }
    }, [isLoaded]);

    let programTable;
    if (appContext.mode == PAGE_MODE.SUMMER_PROGRAMM && selectedSchool) {
        const sortedPrograms = selectedSchool.school._epi.summerPrograms.sort((a, b) => {
            let aName = a.name;
            if (aName == 'HS Credit Recovery' || aName == 'Local Programming') {
                aName = `${a.name} - ${a.programCategories}`
            }
            let bName = b.name;
            if (bName == 'HS Credit Recovery' || bName == 'Local Programming') {
                bName = `${b.name} - ${b.programCategories}`
            }
            return aName.localeCompare(bName)
        });

        programTable =
            <table className="school-programs">
                <thead>
                    <tr>
                        <th>Programs</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedPrograms.map((program, i) => {
                        let displayName = program.name;
                        if (program.name == 'HS Credit Recovery' || program.name == 'CTE Summer Camps' || displayName == 'Local Programming') {
                            displayName = `${program.name} - ${program.programCategories}`
                        }
                        return <tr key={`program${i}`}>
                            <td>{displayName}</td>
                        </tr>
                    })}
                </tbody>
            </table>
    }

    let phone;
    if (appContext.mode != PAGE_MODE.SUMMER_PROGRAMM && selectedSchool) {
        phone = <div className="">
            <strong>Phone: </strong>{selectedSchool.school._epi.phoneNumber}
        </div>
    }
    let rating;

    if (appContext.mode == PAGE_MODE.PREK && selectedSchool) {
        const qualityRating = selectedSchool.school._epi.facets.qualityRating;

        if (qualityRating && qualityRating.length > 0) {
            const filteredRating = qualityRating.filter(item => item !== 'None');

            if (filteredRating.length > 0) {
                rating = (
                    <div>
                        <strong>Excelerate Quality Rating: </strong> {filteredRating.join(', ')}
                    </div>
                );
            } else {
                rating = "";
            }
        } else {
            rating = "";
        }
    }

    return <>{isLoaded &&
        <SchoolMapContext.Provider
            value={{
                notificationReset: notificationReset,
                closeNotifications: closeNotifications,
                nextNotification: nextNotification
            }}
        >
            <GoogleMap
                mapContainerStyle={containerStyle}
                options={{
                    disableDefaultUI: true,
                    zoomControl: true,
                    styles: mapStyles
                }}
                zoom={adjustedZoom}
                onLoad={onLoad}
            >
                {appContext.selectedOverlay &&
                    getDrawableOverlay(appContext.selectedOverlay)
                }
                {selectedSchool && (<InfoWindow key={`{refreshContents_${selectedSchoolId}`}
                    onCloseClick={() => {
                        setSelectedSchool(null);
                        selectedSchoolId(null);

                        const zipCodeFilter = appContext.filterController.filters.find(i => i.fieldName === 'AddressZipCodeFormatted');
                        const enabledZip = zipCodeFilter && zipCodeFilter.terms.filter(i => i.enabled);
                        const postCodeFilter = appContext.filterController.filters.find(i => i.fieldName === 'PostalCode');
                        const enabledPost = postCodeFilter && postCodeFilter.terms.filter(i => i.enabled);
                        const comunityCodeFilter = appContext.filterController.filters.find(i => i.fieldName === 'Community');
                        const enabledComunityCode = comunityCodeFilter && comunityCodeFilter.terms.filter(i => i.enabled);
                        const networkCodeFilter = appContext.filterController.filters.find(i => i.fieldName === 'Network');
                        const enabledNetworkCode = networkCodeFilter && networkCodeFilter.terms.filter(i => i.enabled);


                        if (enabledZip && enabledZip.length > 0) {
                            appContext.addZipOverlay(enabledZip[0].term);
                        } else if (enabledPost && enabledPost.length > 0) {
                            appContext.addZipOverlay(enabledPost[0].term);
                        } else if (enabledComunityCode && enabledComunityCode.length > 0) {
                            appContext.addCommunityOverlay(enabledComunityCode[0].term)
                        } else if (enabledNetworkCode && enabledNetworkCode.length > 0) {
                            appContext.addNetworkOverlay(enabledNetworkCode[0].term)
                        } else {
                            appContext.resetOverlay();
                        }
                    }}
                    options={{
                        pixelOffset: new google.maps.Size(0, -30)
                    }}
                    position={selectedSchool.position}>
                    <div className={`school-popup ${selectedSchool.school.type}`}>
                        <div className={`${selectedSchool.school.type} ${appContext.mode == PAGE_MODE.GOCPS ? getEsHs(selectedSchool.school) : ''} ${appContext.mode == PAGE_MODE.SUMMER_PROGRAMM ? PAGE_MODE.SUMMER_PROGRAMM : ''}`}>
                            <h3 onClick={() => {
                                if (appContext.mode == PAGE_MODE.GOCPS && schoolProfilePageUrl) {
                                    // @ts-ignore
                                    window.open('' + schoolProfilePageUrl + selectedSchool.school._epi.id)
                                } else {
                                    openSchoolProfile(selectedSchool.school._epi.id)
                                }
                            }} >
                                {appContext.mode == PAGE_MODE.SUMMER_PROGRAMM ? selectedSchool.school._epi.schoolShortName : selectedSchool.school._epi.title}
                            </h3>
                        </div>
                        <div className="school-popup-info">
                            <div className=""><strong>Address: </strong>{selectedSchool.school._epi.address}</div>
                            {phone}
                            {rating}
                            <div className="school-popup-directions">
                                <a href={"https://maps.google.com/maps?daddr=" + selectedSchool.school._epi.address} target="_blank">
                                    DIRECTIONS
                                </a>
                            </div>
                            {programTable}
                            <br />
                            <a onClick={() => {
                                if (appContext.mode == PAGE_MODE.GOCPS && schoolProfilePageUrl) {
                                    // @ts-ignore
                                    window.open('' + schoolProfilePageUrl + selectedSchool.school._epi.id);
                                } else {
                                    openSchoolProfile(selectedSchool.school._epi.id)
                                }
                                
                            }}>MORE INFO</a>
                        </div>
                    </div>
                </InfoWindow>)}

                {appContext.mapController.pin && (<HideLogBoundary><Marker
                    position={appContext.mapController.pin}
                    icon={{
                        url: '/Static-Adage/img/search/yellow-pin-lg.png',
                        anchor: new google.maps.Point(16, 40),
                        scaledSize: new google.maps.Size(32, 40)
                    }}
                /></HideLogBoundary>)}

                {appContext.mapController.pin && (<HideLogBoundary><Circle
                    center={appContext.mapController.pin}
                    radius={appContext.mapController.proximity * METERS_PER_MILE}
                    options={{
                        fillColor: '#d3e2eb',
                        strokeColor: '#d9d9d9'
                    }}
                /></HideLogBoundary>)}
                {getMarkers()}

            </GoogleMap>
        </SchoolMapContext.Provider>}</>
}

export default SchoolMap;